import { Drawer, IconButton, useMediaQuery } from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  MenuBook,
  OpenInNew,
  ZoomIn,
  ZoomOut,
} from '@mui/icons-material';
import { PDF_MAX_ZOOM, PDF_MIN_ZOOM, type PdfViewerRef } from '@thingslog/ui-components';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DocumentsSideBar from './DocumentsSideBar';
import { useTranslation } from 'react-i18next';
import Header from '../../components/header';
import { Pdf } from '../../components/Pdf';

const MOBILE_ZOOM_CONTROLS_HIDE_DELAY = 2400;

const useAutoHidingControls = (
  enabled: boolean,
  hideDelay: number
): {
  visible: boolean;
  show: () => void;
} => {
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [visible, setVisible] = useState(false);

  const clearHideTimeout = useCallback((): void => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const show = useCallback((): void => {
    if (!enabled) {
      return;
    }

    clearHideTimeout();
    setVisible(true);
    hideTimeoutRef.current = setTimeout((): void => {
      setVisible(false);
      hideTimeoutRef.current = null;
    }, hideDelay);
  }, [clearHideTimeout, enabled, hideDelay]);

  useEffect((): (() => void) => {
    if (!enabled) {
      clearHideTimeout();
      setVisible(false);

      return clearHideTimeout;
    }

    show();

    return clearHideTimeout;
  }, [clearHideTimeout, enabled, show]);

  return { visible, show };
};

const DocumentationPDFViewer: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery('(max-width:767px)');
  const pdfRef = useRef<PdfViewerRef>(null);
  const [sidemenuOpenMobile, setSidemenuOpenMobile] = useState(false);
  const [docId, setDocId] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [zoom, setZoom] = useState(PDF_MIN_ZOOM);
  const { visible: mobileZoomControlsVisible, show: showMobileZoomControls } =
    useAutoHidingControls(isMobile, MOBILE_ZOOM_CONTROLS_HIDE_DELAY);

  const docs: DocumentationLink[] = useMemo(
    () => [
      {
        id: 1,
        title: t('documentation_getting_started_training_guide'),
        path: {
          en: 'en/ThingsLog_Getting_Started_Training_Guide_nm_EN.pdf',
          fr: 'fr/[15]ThingsLog_Getting_Started_Training_Guide_nm_FR.pdf',
          bg: 'bg/ThingsLog_Getting_Started_Training_Guide_nm_BG.pdf',
          de: 'de/ThingsLog_Getting_Started_Training_Guide_nm_DE.pdf',
          es: 'es/ThingsLog_Getting_Started_Training_Guide_nm_ES.pdf',
          gr: 'ge/ThingsLog_Getting_Started_Training_Guide_nm_EL.pdf',
          ptBr: 'ptBr/ThingsLog_Getting_Started_Training_Guide_nm_PT.pdf',
          ru: 'ru/ThingsLog_Getting_Started_Training_Guide_nm_RU.pdf',
        },
        defaultLang: 'en',
      },
      {
        id: 2,
        title: t('documentation_sign_in_main_menu'),
        path: {
          en: 'en/sign-in-and-main-menu.pdf',
          fr: 'fr/[01]Connexion_et_Menu_Principal.pdf',
        },
        defaultLang: 'en',
      },
      {
        id: 3,
        title: t('documentation_account_management'),
        path: {
          en: 'en/thingslog-account-management.pdf',
          fr: 'fr/[02]Gestion_des_Comptes.pdf',
        },
        defaultLang: 'en',
      },
      {
        id: 4,
        title: t('documentation_user_management'),
        path: {
          en: 'en/thingslog-user-management.pdf',
          fr: 'fr/[03]Gestion_des_Utilisateurs.pdf',
        },
        defaultLang: 'en',
      },
      {
        id: 5,
        title: t('documentation_data_loggers_configuration'),
        path: {
          en: 'en/thingslog-data-loggers-configuration.pdf',
          fr: 'fr/[04]Configuration_des _Data_Logger.pdf',
        },
        defaultLang: 'en',
      },
      {
        id: 6,
        title: t('documentation_alarms_value_range_events'),
        path: {
          en: 'en/configuring-and-reviewing-things-log-alarms-and-value-range-events.pdf',
          fr: 'fr/[05]Gestion_des_Alarmes_et_des_Evenements.pdf',
        },
        defaultLang: 'en',
      },
      {
        id: 7,
        title: t('documentation_modbus_slave_devices'),
        path: {
          en: 'en/port-sensor-parameter-builder-for-modbus-slave-devices.pdf',
          fr: 'fr/[06]Gestion_des_dispositifs_esclaves_Modbus.pdf',
        },
        defaultLang: 'en',
      },
      {
        id: 8,
        title: t('documentation_tasmota_devices'),
        path: {
          en: 'en/setting-up-tasmota-devices-to-work-with-thingslog-platform.pdf',
          fr: 'fr/[07]Configuration_des_dispositifs_Tasmota.pdf',
        },
        defaultLang: 'en',
      },
      {
        id: 9,
        title: t('documentation_modbus_setup'),
        path: {
          en: 'en/setting-up-modbus-slave-devices-to-work-with-thingslog-controllers.pdf',
          fr: 'fr/[08]Configuration_des_dispositifs_esclaves_Modbus.pdf',
        },
        defaultLang: 'en',
      },
      {
        id: 10,
        title: t('documentation_licenses'),
        path: {
          en: 'en/licenses.pdf',
          fr: 'fr/[09]Gestion_des_Licences.pdf',
        },
        defaultLang: 'en',
      },
      {
        id: 11,
        title: t('documentation_old_user_manual'),
        path: {
          en: 'en/tl-en.pdf',
          fr: 'fr/[10]Manuel_Utilisateur_Plateforme_Thingslog.pdf',
        },
        defaultLang: 'en',
      },
      {
        id: 12,
        title: t('documentation_data_logger_manual'),
        path: {
          en: 'en/LPMDL-110x-EN_v1.10.2024.pdf',
          fr: 'fr/[11]LPMDL-110x-FR_Manuel_Utilisateur.pdf',
        },
        defaultLang: 'en',
      },
      {
        id: 13,
        title: t('documentation_data_logger_command_guide'),
        path: {
          en: 'en/LPMDL_110x_command_guide-EN-4.9.6.pdf',
          fr: 'fr/[12]LPMDL-110x-FR_Guide_Commandes_En_Lignes.pdf',
        },
        defaultLang: 'en',
      },
      {
        id: 14,
        title: t('documentation_lora_protocol'),
        path: {
          en: 'en/LPMDL-110X-LoRa-open-data-protocol.pdf',
          fr: 'fr/[13]LPMDL-110x-FR_Protocole_LoRa.pdf',
        },
        defaultLang: 'en',
      },
      {
        id: 15,
        title: t('documentation_mqtt_protocol'),
        path: {
          en: 'en/LPMDL-110x-MQTT-open-data-protocol.pdf',
          fr: 'fr/[14]LPMDL-110x-FR_Protocole_MQTT.pdf',
        },
        defaultLang: 'en',
      },
    ],
    [t]
  );

  const docUrl = useMemo(() => {
    const selectedDoc = docs.find((d: DocumentationLink) => d.id === docId);
    if (!selectedDoc) return '';

    let filePath: string;
    const defaultPath = selectedDoc.path[selectedDoc.defaultLang];
    if (!defaultPath) {
      throw new Error(`Default path for document ${selectedDoc.title} is not defined`);
    }

    switch (i18n.language) {
      case 'fr':
        filePath = selectedDoc.path.fr ?? defaultPath;
        break;
      case 'en':
        filePath = selectedDoc.path.en ?? defaultPath;
        break;
      case 'bg':
        filePath = selectedDoc.path.bg ?? defaultPath;
        break;
      case 'gr':
        filePath = selectedDoc.path.gr ?? defaultPath;
        break;
      case 'de':
        filePath = selectedDoc.path.de ?? defaultPath;
        break;
      case 'es':
        filePath = selectedDoc.path.es ?? defaultPath;
        break;
      case 'ru':
        filePath = selectedDoc.path.ru ?? defaultPath;
        break;
      case 'ptBr':
        filePath = selectedDoc.path.ptBr ?? defaultPath;
        break;
      default:
        filePath = selectedDoc.path.en ?? defaultPath;
        break;
    }

    return `/docs/${filePath}`;
  }, [docId, docs, i18n.language]);

  const documentFileName = useMemo((): string => {
    return decodeURIComponent(docUrl.split('/').pop() || 'document.pdf');
  }, [docUrl]);

  const handleSelect = (currentDocumentId: number): void => {
    setDocId(currentDocumentId);
    setCurrentPage(1);
    setTotalPages(0);
    setZoom(PDF_MIN_ZOOM);
    showMobileZoomControls();
    setSidemenuOpenMobile(false);
  };

  const selectedDocumentTitle = useMemo((): string => {
    return docs.find((doc: DocumentationLink) => doc.id === docId)?.title ?? '';
  }, [docId, docs]);

  const handlePreviousPage = (): void => {
    pdfRef.current?.prevPage();
  };

  const handleNextPage = (): void => {
    pdfRef.current?.nextPage();
  };

  const handleZoomOut = (): void => {
    pdfRef.current?.zoomOut();
    showMobileZoomControls();
  };

  const handleZoomIn = (): void => {
    pdfRef.current?.zoomIn();
    showMobileZoomControls();
  };

  const handleDownload = async (): Promise<void> => {
    const response = await fetch(docUrl);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = objectUrl;
    link.download = documentFileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
  };

  const handleViewInNewTab = (): void => {
    window.open(docUrl, '_blank');
  };

  return (
    <Header fullHeightContent>
      <div className="relative flex h-full min-h-0 w-full max-w-full flex-col overflow-hidden text-left md:flex-row">
        <DocumentsSideBar
          docs={docs}
          currentDocId={docId}
          onSelect={handleSelect}
          className="hidden h-full w-[280px] shrink-0 border-0 border-r border-solid border-gray-200 md:!flex"
        />
        <Drawer
          anchor="left"
          open={sidemenuOpenMobile}
          onClose={(): void => setSidemenuOpenMobile(false)}
          ModalProps={{
            disablePortal: true,
          }}
          PaperProps={{
            className: 'w-[280px] max-w-[85vw]',
          }}
        >
          <DocumentsSideBar
            docs={docs}
            currentDocId={docId}
            onSelect={handleSelect}
            className="h-full w-full"
          />
        </Drawer>
        <section className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-gray-100">
          <div className="hidden shrink-0 flex-col gap-3 border-0 border-b border-solid border-gray-200 bg-white px-4 py-3 md:!flex md:flex-row md:items-center md:justify-between">
            <div className="flex min-w-0 items-center gap-2">
              <h1 className="m-0 min-w-0 truncate text-lg font-semibold text-gray-900">
                {selectedDocumentTitle}
              </h1>
              <IconButton aria-label="Download document" onClick={handleDownload} size="small">
                <Download />
              </IconButton>
              <IconButton
                aria-label="Open document in new tab"
                onClick={handleViewInNewTab}
                size="small"
              >
                <OpenInNew />
              </IconButton>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <IconButton
                aria-label="Previous page"
                size="small"
                onClick={handlePreviousPage}
                disabled={currentPage <= 1}
              >
                <ChevronLeft />
              </IconButton>
              <span className="min-w-[72px] text-center text-sm font-medium text-gray-700">
                {currentPage} / {totalPages || '-'}
              </span>
              <IconButton
                aria-label="Next page"
                size="small"
                onClick={handleNextPage}
                disabled={totalPages === 0 || currentPage >= totalPages}
              >
                <ChevronRight />
              </IconButton>
            </div>
          </div>
          <div
            className="relative min-h-0 min-w-0 flex-1 overflow-hidden p-1 md:p-4"
            onTouchMove={showMobileZoomControls}
            onTouchStart={showMobileZoomControls}
          >
            <Pdf
              ref={pdfRef}
              documentUrl={docUrl}
              scaleMode="fit-width"
              className="md:mx-auto md:max-w-[1280px]"
              onLoad={(pages: number): void => {
                setTotalPages(pages);
                setCurrentPage(1);
              }}
              onPageChange={(page: number, pages: number): void => {
                setCurrentPage(page);
                setTotalPages(pages);
              }}
              onZoomChange={setZoom}
            />
            <div
              className={`absolute right-3 top-3 z-10 flex rounded bg-white/90 shadow-md ring-1 ring-black/10 transition-opacity duration-200 md:hidden ${
                mobileZoomControlsVisible ? 'opacity-100' : 'pointer-events-none opacity-0'
              }`}
              onTouchStart={showMobileZoomControls}
            >
              <IconButton
                aria-label="Zoom out"
                size="small"
                onClick={handleZoomOut}
                disabled={zoom <= PDF_MIN_ZOOM}
              >
                <ZoomOut />
              </IconButton>
              <IconButton
                aria-label="Zoom in"
                size="small"
                onClick={handleZoomIn}
                disabled={zoom >= PDF_MAX_ZOOM}
              >
                <ZoomIn />
              </IconButton>
            </div>
          </div>
          <div className="grid shrink-0 grid-cols-[auto_1fr_auto] items-center gap-1 border-0 border-t border-solid border-gray-200 bg-white px-2 py-1 md:hidden">
            <IconButton
              aria-label="Open documents list"
              size="small"
              onClick={(): void => setSidemenuOpenMobile(true)}
              className="justify-self-start"
            >
              <MenuBook />
            </IconButton>
            <div className="flex items-center justify-center gap-2">
              <IconButton
                aria-label="Previous page"
                size="small"
                onClick={handlePreviousPage}
                disabled={currentPage <= 1}
              >
                <ChevronLeft />
              </IconButton>
              <span className="min-w-[72px] text-center text-sm font-medium text-gray-700">
                {currentPage} / {totalPages || '-'}
              </span>
              <IconButton
                aria-label="Next page"
                size="small"
                onClick={handleNextPage}
                disabled={totalPages === 0 || currentPage >= totalPages}
              >
                <ChevronRight />
              </IconButton>
            </div>
            <IconButton
              aria-label="Open document in new tab"
              className="justify-self-end"
              onClick={handleViewInNewTab}
              size="small"
            >
              <OpenInNew />
            </IconButton>
          </div>
        </section>
      </div>
    </Header>
  );
};

export interface DocumentationLink {
  id: number;
  title: string;
  path: Partial<Record<Language, string>>;
  defaultLang: Language;
}

type Language = 'en' | 'fr' | 'bg' | 'de' | 'es' | 'gr' | 'ptBr' | 'ru';

export default DocumentationPDFViewer;
